import { AppRegistry } from 'react-native';
import { LoadSkiaWeb } from '@shopify/react-native-skia/lib/module/web';
import { z } from 'zod';
import { name as appName } from './app.json';
import { installGlobalErrorHandlers } from './src/platform/global-error-handler';

installGlobalErrorHandlers();

// The PWA's Content-Security-Policy has no 'unsafe-eval'. zod probes for
// `new Function` to compile a fast path for object schemas, and the browser
// reports that probe as a CSP violation. zod reads this setting when a schema
// is created, so it must run before App (imported below) is evaluated.
z.config({ jitless: true });

/** Escape HTML special characters to prevent XSS in error displays. */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Add global error handler for debugging. Only replace the page content
// when the app has not rendered yet (boot failure); once the app is up,
// the React ErrorBoundary owns render errors and other uncaught errors
// are recoverable, so wiping the UI would make things worse.
window.onerror = function (message, source, lineno, colno, error) {
  console.error('Global error:', message, source, lineno, colno, error);
  const root = document.getElementById('root');
  if (root && root.childElementCount === 0) {
    root.innerHTML = `
      <div style="padding: 20px; color: red; font-family: monospace;">
        <h2>Error</h2>
        <pre>${escapeHtml(message)}\n${escapeHtml(
      source,
    )}:${lineno}:${colno}</pre>
      </div>
    `;
  }
  return false;
};

// Load CanvasKit WASM first, then dynamically import the App.
// This ordering is critical: Skia.web.js evaluates
// `JsiSkApi(global.CanvasKit)` at module scope, so global.CanvasKit
// must be set BEFORE any module that imports @shopify/react-native-skia
// is evaluated.
LoadSkiaWeb({ locateFile: () => '/static/js/canvaskit.wasm' })
  .then(() => import('./App'))
  .then(({ default: App }) => {
    AppRegistry.registerComponent(appName, () => App);
    AppRegistry.runApplication(appName, {
      initialProps: {},
      rootTag: document.getElementById('root'),
    });
  })
  .catch(err => {
    console.error('Failed to load CanvasKit:', err);
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="padding: 40px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #333;">
          <h2 style="margin-bottom: 16px;">Failed to load graphics engine</h2>
          <p style="margin-bottom: 24px;">CanvasKit (Skia) could not be initialized. Please try refreshing the page.</p>
          <button onclick="location.reload()" style="padding: 10px 24px; font-size: 16px; cursor: pointer; border: 1px solid #ccc; border-radius: 6px; background: #f5f5f5;">
            Reload
          </button>
          <pre style="margin-top: 24px; font-size: 12px; color: #999; white-space: pre-wrap;">${escapeHtml(
            err,
          )}</pre>
        </div>
      `;
    }
  });

// Register service worker for PWA
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(error => {
      console.error('Service worker registration failed:', error);
    });
  });
}
